import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from '../../../context/BusinessContext';
import { useNavigation } from '../../../context/NavigationContext';
import { MENU } from '../../util/Utils';
import { useTimeFormat, formatClock } from '../../../util/timeFormat';
import { useAlert } from '../../../context/alert/AlertContext';
import LoadingComponent from '../../commons/LoadingComponent';
import PayoutInfoHint from '../../commons/payout-hint/PayoutInfoHint';
import { Appointment } from '../../../model/appointment/Appointment';
import { APPOINTMENT_PAYMENT_LABEL } from '../../../model/appointment/AppointmentPaymentStatus';
import { getAppointmentById } from '../../../service/appointment/appointment-service';
import Drawer from '../../commons/drawer/Drawer';
import Pagination from '../../commons/pagination/Pagination';
import {
    ManualPaymentInput,
    PaymentMethod,
    Transaction,
    TransactionFilters,
    TransactionStatus,
    TRANSACTION_STATUS_LABEL,
    TransactionStats,
    parseAcceptedMethods,
} from '../../../model/payment/Transaction';
import {
    getTransactions,
    getTransactionStats,
    recordManualPayment,
    refundTransaction,
    updateManualPayment,
    deleteManualPayment,
    getTransactionsByAppointment,
} from '../../../service/payment/transaction-service';
import { Client } from '../../../model/client/Client';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import { getMyPermissionGrant, PermissionGrant } from '../../../service/permission/permission-service';
import { currencyCode, CURRENCIES } from '../../../util/money';
import MoneyInput from '../../commons/money-input/MoneyInput';
import ConfirmModal from '../../commons/confirm-modal/ConfirmModal';
import './PaymentsComponent.css';

/**
 * Format an ISO date to "YYYY-MM-DD" for <input type="date"> values.
 */
const toDateInputValue = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** First day of the current month as Date. */
/* --------- Date-range presets (This month / Last month / etc.) --------- */
type DatePreset =
    | 'TODAY'
    | 'YESTERDAY'
    | 'THIS_WEEK'
    | 'THIS_MONTH'
    | 'LAST_MONTH'
    | 'THIS_QUARTER'
    | 'LAST_QUARTER'
    | 'THIS_YEAR'
    | 'LAST_YEAR'
    | 'CUSTOM';

const DATE_PRESET_KEYS: DatePreset[] = [
    'TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH', 'LAST_MONTH',
    'THIS_QUARTER', 'LAST_QUARTER', 'THIS_YEAR', 'LAST_YEAR', 'CUSTOM',
];

/** Resolve a preset to its [startDate, endDate] strings (inclusive). */
const presetRange = (p: DatePreset): { start: string; end: string } => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const ymd = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const d = today.getDay(); // 0=Sun..6=Sat — week starts on Monday
    const mondayOffset = (d + 6) % 7;
    switch (p) {
        case 'TODAY':
            return { start: ymd(today), end: ymd(today) };
        case 'YESTERDAY': {
            const y1 = new Date(today);
            y1.setDate(today.getDate() - 1);
            return { start: ymd(y1), end: ymd(y1) };
        }
        case 'THIS_WEEK': {
            const start = new Date(today);
            start.setDate(today.getDate() - mondayOffset);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return { start: ymd(start), end: ymd(end) };
        }
        case 'THIS_MONTH':
            return { start: ymd(new Date(y, m, 1)),       end: ymd(new Date(y, m + 1, 0)) };
        case 'LAST_MONTH':
            return { start: ymd(new Date(y, m - 1, 1)),   end: ymd(new Date(y, m, 0)) };
        case 'THIS_QUARTER': {
            const qStart = Math.floor(m / 3) * 3;
            return { start: ymd(new Date(y, qStart, 1)),  end: ymd(new Date(y, qStart + 3, 0)) };
        }
        case 'LAST_QUARTER': {
            const qStart = Math.floor(m / 3) * 3 - 3;
            const qy = qStart < 0 ? y - 1 : y;
            const realStart = ((qStart % 12) + 12) % 12;
            return { start: ymd(new Date(qy, realStart, 1)), end: ymd(new Date(qy, realStart + 3, 0)) };
        }
        case 'THIS_YEAR':
            return { start: ymd(new Date(y, 0, 1)),       end: ymd(new Date(y, 11, 31)) };
        case 'LAST_YEAR':
            return { start: ymd(new Date(y - 1, 0, 1)),   end: ymd(new Date(y - 1, 11, 31)) };
        case 'CUSTOM':
        default:
            return { start: '', end: '' };
    }
};

const firstOfMonth = (): Date => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
};

/** Add days to a Date (returns new Date). */
const addDays = (d: Date, days: number): Date => {
    const c = new Date(d);
    c.setDate(c.getDate() + days);
    return c;
};

/** Format money as "$1,234.56" — currency mostly cosmetic for now. */
const fmtMoney = (n: number, currency = 'USD'): string => {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
        }).format(n);
    } catch {
        return `${currency} ${n.toFixed(2)}`;
    }
};

const PaymentsComponent: React.FC = () => {
    const { t } = useTranslation();
    const timeFmt = useTimeFormat();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setActiveMenu, setPendingAppointmentId, pendingPaymentAppointmentId, setPendingPaymentAppointmentId } = useNavigation();

    /**
     * Bounce the user to the Appointments screen with the target appointment
     * pre-selected. AppointmentsComponent picks up pendingAppointmentId,
     * opens its detail drawer for that appointment, and clears the flag.
     */
    const openAppointmentDetail = (appointmentId: string) => {
        setPendingAppointmentId(appointmentId);
        setActiveMenu(MENU.APPOINTMENTS);
    };
    const { setAlert } = useAlert();
    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    /** ISO currency code for the active business — drives the stats cards
     *  and the manual-payment form default. Transactions still carry their
     *  own currency on each row (which can differ if you change settings
     *  later). */
    const businessCurrency = currencyCode(selectedBusiness?.currency);

    // ----- Filters / pagination state -----
    /** Selected preset. From/To inputs only show when "Custom range" is picked. */
    const [datePreset, setDatePreset] = useState<DatePreset>('THIS_MONTH');
    const initialThisMonth = presetRange('THIS_MONTH');
    const [startDate, setStartDate] = useState<string>(initialThisMonth.start);
    const [endDate, setEndDate] = useState<string>(initialThisMonth.end);
    const [methodFilter, setMethodFilter] = useState<PaymentMethod | ''>('');
    const [statusFilter, setStatusFilter] = useState<TransactionStatus | ''>('');
    const [search, setSearch] = useState('');
    /** Two independent id searches (issue #43): the payment's own id and the
     *  appointment id are NEVER conflated. Each is exact and ignores the date
     *  range (an id is global). When both are filled the backend OR-matches them. */
    const [paymentIdSearch, setPaymentIdSearch] = useState('');
    const [apptIdSearch, setApptIdSearch] = useState('');
    /** Debounced copies actually sent to the backend — the typed value updates
     *  the input instantly while the query waits until typing settles (400ms).
     *  Without this every keystroke re-ran the query and the list re-render
     *  stole focus from the id field (same issue fixed in Appointments #44). */
    const [appliedPaymentId, setAppliedPaymentId] = useState('');
    const [appliedApptId, setAppliedApptId] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    // ----- Data -----
    const [stats, setStats] = useState<TransactionStats | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    /** appointmentId → Appointment (for showing paymentStatus per row). */
    const [appointmentMap, setAppointmentMap] = useState<Record<string, Appointment>>({});
    /** appointmentId → amount still due (appointmentTotal snapshot − net paid). */
    const [remainingByAppt, setRemainingByAppt] = useState<Record<string, number>>({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    /** Set after the first load so subsequent reloads (filtering/typing) keep the
     *  filters + table mounted instead of swapping in the full-screen spinner,
     *  which would unmount the inputs and drop focus mid-type. */
    const [didInitialLoad, setDidInitialLoad] = useState(false);
    const [saving, setSaving] = useState(false);

    // ----- Manual payment drawer -----
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [draft, setDraft] = useState<ManualPaymentInput | null>(null);
    /** When set, the drawer is editing an existing transaction (not creating). */
    const [editingId, setEditingId] = useState<string | null>(null);

    // ----- Refund + delete confirmation modals -----
    /** Transaction the refund modal is pointed at; null = closed. */
    const [refundTarget, setRefundTarget] = useState<Transaction | null>(null);
    /** Controlled value of the refund-amount input. Empty = full refund. */
    const [refundInput, setRefundInput] = useState<string>('');
    const [refundBusy, setRefundBusy] = useState(false);
    /** Transaction the delete modal is pointed at; null = closed. */
    const [deleteTarget, setDeleteTarget] = useState<Transaction | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // ----- Clients (for the customer-name autocomplete inside the manual payment drawer) -----
    const [clients, setClients] = useState<Client[]>([]);
    const [showClientDropdown, setShowClientDropdown] = useState(false);

    /**
     * The caller's REFUNDS grant. Refunds are a separate, stricter permission
     * than PAYMENTS — a team member can have read/write on payments yet not be
     * allowed to issue refunds. null = not loaded yet → treat as NOT allowed
     * (fail-closed), so the Refund button never flashes for an unauthorized user.
     * The backend re-checks REFUNDS on refundTransaction; this is UX only.
     */
    const [refundsGrant, setRefundsGrant] = useState<PermissionGrant | null>(null);
    const canRefund = !!refundsGrant?.canEdit;

    /**
     * The caller's CHECKOUT grant — gates recording/editing/deleting payments
     * (the "Record payment" button, the manual-payment drawer, and per-row
     * Edit/Delete). null = not loaded → fail-closed (no record actions). The
     * backend re-checks CHECKOUT on every record/update/delete mutation.
     */
    const [checkoutGrant, setCheckoutGrant] = useState<PermissionGrant | null>(null);
    const canCheckout = !!checkoutGrant?.canEdit;

    // Load clients once for autocomplete inside the manual-payment drawer.
    useEffect(() => {
        if (!businessId) return;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const list = await getAllClientsByBusinessId(businessId, token);
                setClients(list);
            } catch {
                // Soft fail — autocomplete just shows nothing.
            }
        })();
    }, [businessId, getAccessTokenSilently]);

    // Load the caller's REFUNDS + CHECKOUT grants to gate the payment actions.
    useEffect(() => {
        if (!businessId) { setRefundsGrant(null); setCheckoutGrant(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const [refunds, checkout] = await Promise.all([
                    getMyPermissionGrant(businessId, 'REFUNDS', token).catch(() => null),
                    getMyPermissionGrant(businessId, 'CHECKOUT', token).catch(() => null),
                ]);
                if (!cancelled) { setRefundsGrant(refunds); setCheckoutGrant(checkout); }
            } catch {
                if (!cancelled) { setRefundsGrant(null); setCheckoutGrant(null); } // fail-closed
            }
        })();
        return () => { cancelled = true; };
    }, [businessId, getAccessTokenSilently]);

    const clientMatches = useMemo(() => {
        if (!draft?.customerName) return [] as Client[];
        const q = draft.customerName.trim().toLowerCase();
        if (q.length < 2) return [];
        // Don't show suggestions when the typed name already matches a linked client.
        if (draft.clientId) return [];
        return clients
            .filter((c) => {
                const full = `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase();
                return (
                    full.includes(q) ||
                    (c.email ?? '').toLowerCase().includes(q) ||
                    (c.phone ?? '').toLowerCase().includes(q)
                );
            })
            .slice(0, 6);
    }, [draft?.customerName, draft?.clientId, clients]);

    /** Build [start, end) ISO datetimes from the YYYY-MM-DD inputs. */
    const dateRange = useMemo(() => {
        // Treat the inputs as local-day boundaries and convert to ISO
        // local datetimes the backend can parse with LocalDateTime.parse.
        const startIso = startDate ? `${startDate}T00:00:00` : null;
        // Build the "next day" by parts to dodge a timezone bug:
        // `new Date("YYYY-MM-DD")` parses as UTC, so on UTC-negative
        // locales addDays(+1) still leaves you short — the last day of
        // the selected range was getting silently excluded.
        let endIso: string | null = null;
        if (endDate) {
            const [yy, mm, dd] = endDate.split('-').map(Number);
            const next = new Date(yy, mm - 1, dd);
            next.setDate(next.getDate() + 1);
            endIso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}T00:00:00`;
        }
        return { startIso, endIso };
    }, [startDate, endDate]);

    // Debounce both id boxes into their applied copies — the query waits until
    // typing settles instead of firing per keystroke.
    useEffect(() => {
        const h = setTimeout(() => setAppliedPaymentId(paymentIdSearch.trim()), 400);
        return () => clearTimeout(h);
    }, [paymentIdSearch]);
    useEffect(() => {
        const h = setTimeout(() => setAppliedApptId(apptIdSearch.trim()), 400);
        return () => clearTimeout(h);
    }, [apptIdSearch]);

    const filters: TransactionFilters = useMemo(
        () => {
            const payId = appliedPaymentId.trim();
            const apptId = appliedApptId.trim();
            return {
                start: dateRange.startIso,
                end: dateRange.endIso,
                status: statusFilter || null,
                method: methodFilter || null,
                // Each id field maps to exactly one backend field — the payment id
                // box never matches an appointment id and vice-versa (issue #43).
                // Omitted when empty so normal browsing keeps the date range.
                ...(payId ? { id: payId } : {}),
                ...(apptId ? { appointmentId: apptId } : {}),
                search: search.trim() || null,
                page,
                perPage,
            };
        },
        [dateRange.startIso, dateRange.endIso, statusFilter, methodFilter, appliedPaymentId, appliedApptId, search, page, perPage]
    );

    // Arriving from the Appointments screen ("ver pagamentos"): pre-filter to
    // that appointment's payments, then clear the flag so it doesn't re-trigger.
    useEffect(() => {
        if (pendingPaymentAppointmentId) {
            setApptIdSearch(pendingPaymentAppointmentId); // exact appointment match
            setAppliedApptId(pendingPaymentAppointmentId); // apply now — no debounce wait for deep-links
            setPaymentIdSearch('');
            setAppliedPaymentId('');
            setPage(1);
            setPendingPaymentAppointmentId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPaymentAppointmentId]);

    const reload = useCallback(async () => {
        if (!businessId) return;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [tx, st] = await Promise.all([
                getTransactions(businessId, filters, token),
                dateRange.startIso && dateRange.endIso
                    ? getTransactionStats(businessId, dateRange.startIso, dateRange.endIso, token)
                    : Promise.resolve(null),
            ]);
            setTransactions(tx.items);
            setTotal(tx.total);
            setStats(st);

            // Pull the appointments referenced by this page so each row can
            // render its payment-status badge. We fetch by the unique
            // appointmentIds in the current page (not by date range —
            // an appointment can sit outside the filter window even though
            // a transaction inside it points at it).
            try {
                const ids = Array.from(
                    new Set(
                        tx.items
                            .map((row) => row.appointmentId)
                            .filter((id): id is string => !!id)
                    )
                );
                if (ids.length > 0) {
                    const results = await Promise.all(
                        ids.map((id) =>
                            Promise.all([
                                getAppointmentById(id, token).catch(() => null),
                                getTransactionsByAppointment(id, token).catch(() => [] as Transaction[]),
                            ]).then(([appt, txs]) => ({ id, appt, txs }))
                        )
                    );
                    const map: Record<string, Appointment> = {};
                    const rem: Record<string, number> = {};
                    for (const { id, appt, txs } of results) {
                        if (appt) map[String(appt.id)] = appt;
                        // Snapshot total comes from the most recent txn that has it;
                        // sum net-paid across SUCCEEDED / PARTIALLY_REFUNDED.
                        const total = txs.reduce((acc, row) => {
                            const v = row.appointmentTotal;
                            return v != null && v > acc ? v : acc;
                        }, 0);
                        const paid = txs.reduce((s, row) => {
                            if (row.status === 'SUCCEEDED' || row.status === 'PARTIALLY_REFUNDED') {
                                return s + (row.amount - (row.refundedAmount || 0));
                            }
                            return s;
                        }, 0);
                        const left = +(total - paid).toFixed(2);
                        if (total > 0 && left > 0) rem[id] = left;
                    }
                    setAppointmentMap(map);
                    setRemainingByAppt(rem);
                } else {
                    setAppointmentMap({});
                    setRemainingByAppt({});
                }
            } catch {
                setAppointmentMap({});
                setRemainingByAppt({});
            }
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payments.alerts.loadError'));
        } finally {
            setLoading(false);
            setDidInitialLoad(true);
        }
    }, [businessId, filters, dateRange.startIso, dateRange.endIso, getAccessTokenSilently, setAlert]);

    useEffect(() => {
        reload();
    }, [reload]);

    // ----- Manual payment -----

    const openManualPayment = () => {
        if (!businessId) {
            setAlert('danger', t('payments.alerts.noBusinessSelected'));
            return;
        }
        setDraft({
            businessId,
            customerName: '',
            amount: 0,
            // Default to the business's configured currency so the drawer
            // matches what the user sees on the calendar / settings.
            currency: currencyCode(selectedBusiness?.currency),
            method: 'CASH',
            description: '',
            notes: '',
        });
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setDraft(null);
        setEditingId(null);
    };

    const handleSaveManual = async () => {
        if (!draft) return;
        if (!canCheckout) {
            // Defense-in-depth: drawer is unreachable without CHECKOUT, but never
            // call the mutation if the grant says no. Backend enforces too.
            setAlert('danger', t('payments.alerts.noCheckoutPermission'));
            closeDrawer();
            return;
        }
        if (!draft.amount || draft.amount <= 0) {
            setAlert('danger', t('payments.alerts.amountRequired'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            if (editingId) {
                // Editing an existing manual payment.
                await updateManualPayment(
                    {
                        id: editingId,
                        customerName: draft.customerName,
                        amount: draft.amount,
                        discountAmount: draft.discountAmount ?? null,
                        method: draft.method,
                        description: draft.description ?? null,
                        notes: draft.notes ?? null,
                    },
                    token
                );
                setAlert('success', t('payments.alerts.updated'));
            } else {
                if (draft.method === 'GIFT_CARD' && !(draft.giftCardCode ?? '').trim()) {
                    setAlert('danger', t('payments.giftCard.codeRequired'));
                    return;
                }
                await recordManualPayment(draft, token);
                setAlert('success', t('payments.alerts.recorded'));
            }
            closeDrawer();
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payments.alerts.saveError'));
        } finally {
            setSaving(false);
        }
    };

    /** Open the drawer pre-populated with an existing transaction for editing. */
    const openEditPayment = (tx: Transaction) => {
        if (tx.method === 'CARD_STRIPE') {
            setAlert('danger', t('payments.alerts.cantEditStripe'));
            return;
        }
        if (tx.refundedAmount > 0) {
            setAlert('danger', t('payments.alerts.cantEditRefunded'));
            return;
        }
        // A received + approved payment is settled — only a still-PENDING entry
        // (e.g. an unreceived convênio receivable) can be edited. Mirrors the
        // backend guard in TransactionService.updateManual.
        if (tx.status !== 'PENDING') {
            setAlert('danger', t('payments.alerts.cantEditSettled'));
            return;
        }
        setEditingId(tx.id);
        setDraft({
            businessId: tx.businessId,
            appointmentId: tx.appointmentId ?? null,
            clientId: tx.clientId ?? null,
            customerName: tx.customerName ?? '',
            amount: tx.amount,
            currency: tx.currency,
            discountAmount: tx.discountAmount,
            method: tx.method,
            description: tx.description ?? '',
            notes: tx.notes ?? '',
        });
        setDrawerOpen(true);
    };

    /** Open the delete-confirmation modal for a manual payment. The actual
     *  API call lives in {@link confirmDelete} below — separating intent
     *  from execution lets the modal own its busy/disable state. */
    const handleDeletePayment = (tx: Transaction) => {
        if (tx.method === 'CARD_STRIPE') {
            setAlert('danger', t('payments.alerts.cantDeleteStripe'));
            return;
        }
        if (tx.refundedAmount > 0) {
            setAlert('danger', t('payments.alerts.cantDeleteRefunded'));
            return;
        }
        setDeleteTarget(tx);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        if (!canCheckout) {
            setAlert('danger', t('payments.alerts.noCheckoutPermission'));
            setDeleteTarget(null);
            return;
        }
        try {
            setDeleteBusy(true);
            const token = await getAccessTokenSilently();
            await deleteManualPayment(deleteTarget.id, token);
            setAlert('success', t('payments.alerts.deleted'));
            setDeleteTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payments.alerts.deleteError'));
        } finally {
            setDeleteBusy(false);
        }
    };

    /** Open the refund modal. Confirmation lives in {@link confirmRefund}. */
    const handleRefund = (t: Transaction) => {
        setRefundTarget(t);
        setRefundInput('');
    };

    const confirmRefund = async () => {
        if (!refundTarget) return;
        if (!canRefund) {
            // Defense-in-depth: the button is hidden without REFUNDS, but never
            // call the mutation if the grant says no. Backend enforces too.
            setAlert('danger', t('payments.alerts.noRefundPermission'));
            setRefundTarget(null);
            return;
        }
        const trimmed = refundInput.trim();
        const amount = trimmed ? Number(trimmed) : null;
        if (amount !== null && (isNaN(amount) || amount <= 0)) {
            setAlert('danger', t('payments.alerts.invalidRefundAmount'));
            return;
        }
        const remaining = refundTarget.amount - refundTarget.refundedAmount;
        if (amount !== null && amount > remaining) {
            setAlert('danger', t('payments.alerts.refundExceeds', { amount: fmtMoney(remaining, refundTarget.currency) }));
            return;
        }
        try {
            setRefundBusy(true);
            const token = await getAccessTokenSilently();
            await refundTransaction(refundTarget.id, amount, token);
            setAlert('success', t('payments.alerts.refunded'));
            setRefundTarget(null);
            await reload();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('payments.alerts.refundError'));
        } finally {
            setRefundBusy(false);
        }
    };

    if (loading && !didInitialLoad) return <LoadingComponent />;

    return (
        <div className="payments-page" data-cy="payments-page">

            {/* ---------- Stats cards ---------- */}
            <div className="payments-stats">
                <div className="payments-stat">
                    <div className="payments-stat__label">{t('payments.stats.gross')}</div>
                    <div className="payments-stat__value">
                        {fmtMoney(stats?.grossTotal ?? 0, businessCurrency)}
                    </div>
                </div>
                <div className="payments-stat">
                    <div className="payments-stat__label">{t('payments.stats.net')}</div>
                    <div className="payments-stat__value">
                        {fmtMoney(stats?.netTotal ?? 0, businessCurrency)}
                    </div>
                </div>
                <div className="payments-stat">
                    <div className="payments-stat__label">{t('payments.stats.refunded')}</div>
                    <div className="payments-stat__value payments-stat__value--neg">
                        {fmtMoney(stats?.refundedTotal ?? 0, businessCurrency)}
                    </div>
                </div>
                <div className="payments-stat" title={t('payments.stats.transactionsTitle')}>
                    <div className="payments-stat__label">{t('payments.stats.transactions')}</div>
                    <div className="payments-stat__value">{stats?.count ?? 0}</div>
                </div>
                <div
                    className="payments-stat"
                    title={t('payments.stats.averageTitle')}
                >
                    <div className="payments-stat__label">{t('payments.stats.average')}</div>
                    <div className="payments-stat__value">
                        {fmtMoney(stats?.averageTicket ?? 0, businessCurrency)}
                    </div>
                </div>
            </div>

            {/* ---------- Filters + Record button ---------- */}
            <div className="list-card payments-table-card">
                <div className="payments-toolbar">
                    <div className="payments-toolbar__filters">
                        <label>
                            {t('payments.filter.period')}
                            <select
                                value={datePreset}
                                data-cy="payments-filter-period"
                                onChange={(e) => {
                                    const next = e.target.value as DatePreset;
                                    setDatePreset(next);
                                    if (next !== 'CUSTOM') {
                                        const r = presetRange(next);
                                        setStartDate(r.start);
                                        setEndDate(r.end);
                                    }
                                    setPage(1);
                                }}
                            >
                                {DATE_PRESET_KEYS.map((k) => (
                                    <option key={k} value={k}>
                                        {t('payments.presets.' + k)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {datePreset === 'CUSTOM' && (
                            <>
                                <label>
                                    {t('payments.filter.from')}
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </label>
                                <label>
                                    {t('payments.filter.to')}
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            setEndDate(e.target.value);
                                            setPage(1);
                                        }}
                                    />
                                </label>
                            </>
                        )}
                        <label>
                            {t('payments.filter.method')}
                            <select
                                value={methodFilter}
                                data-cy="payments-filter-method"
                                onChange={(e) => {
                                    setMethodFilter(e.target.value as PaymentMethod | '');
                                    setPage(1);
                                }}
                            >
                                <option value="">{t('common.all')}</option>
                                {/* Only methods the business accepts — disabled
                                    methods would never appear in real data. */}
                                {parseAcceptedMethods(
                                    selectedBusiness?.acceptedPaymentMethods,
                                    selectedBusiness?.country
                                ).map((k) => (
                                    <option key={k} value={k}>
                                        {t(`payments.method.${k}`)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('payments.filter.status')}
                            <select
                                value={statusFilter}
                                data-cy="payments-filter-status"
                                onChange={(e) => {
                                    setStatusFilter(e.target.value as TransactionStatus | '');
                                    setPage(1);
                                }}
                            >
                                <option value="">{t('common.all')}</option>
                                {Object.entries(TRANSACTION_STATUS_LABEL).map(([k, v]) => (
                                    <option key={k} value={k}>{t(`payments.status.${k}`, { defaultValue: v })}</option>
                                ))}
                            </select>
                        </label>
                        <label className="payments-toolbar__search">
                            {t('payments.filter.customer')}
                            <input
                                type="text"
                                placeholder={t('payments.filter.namePlaceholder')}
                                value={search}
                                data-cy="payments-filter-search"
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                            />
                        </label>
                        <label>
                            {t('payments.filter.paymentId')}
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder={t('payments.filter.paymentIdPlaceholder')}
                                value={paymentIdSearch}
                                data-cy="payments-filter-payment-id"
                                onChange={(e) => {
                                    setPaymentIdSearch(e.target.value.replace(/[^\d]/g, ''));
                                    setPage(1);
                                }}
                            />
                        </label>
                        <label>
                            {t('payments.filter.appointmentId')}
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder={t('payments.filter.appointmentIdPlaceholder')}
                                value={apptIdSearch}
                                data-cy="payments-filter-appointment-id"
                                onChange={(e) => {
                                    setApptIdSearch(e.target.value.replace(/[^\d]/g, ''));
                                    setPage(1);
                                }}
                            />
                        </label>
                    </div>
                    {canCheckout && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={openManualPayment}
                            data-cy="payments-record-btn"
                        >
                            {t('payments.recordBtn')}
                        </button>
                    )}
                </div>

                {/* ---------- Table ---------- */}
                <div className="payments-table-wrap">
                    <table className="payments-table" data-cy="payments-table">
                        <thead>
                            <tr>
                                <th>{t('payments.table.paymentId')}</th>
                                <th>{t('payments.table.when')}</th>
                                <th>{t('payments.table.customer')}</th>
                                <th>{t('payments.table.appointmentBooking')}</th>
                                <th>{t('payments.table.method')}</th>
                                <th>{t('payments.table.status')}</th>
                                <th className="payments-table__num">{t('payments.table.amount')}</th>
                                <th className="payments-table__num">{t('payments.table.refunded')}</th>
                                <th className="payments-table__num">{t('payments.table.net')}</th>
                                <th>{t('payments.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="empty-hint">
                                        {t('payments.empty')}
                                    </td>
                                </tr>
                            )}
                            {transactions.map((tx) => {
                                const created = new Date(tx.createdOn);
                                const refundable =
                                    canRefund &&
                                    !tx.deleted &&
                                    (tx.status === 'SUCCEEDED' ||
                                        tx.status === 'PARTIALLY_REFUNDED') &&
                                    tx.refundedAmount < tx.amount;
                                return (
                                    <tr key={tx.id} className={tx.deleted ? 'payments-row payments-row--deleted' : 'payments-row'} data-cy={`payments-row-${tx.id}`}>
                                        <td>
                                            <span className="payments-pay-id" data-cy={`payments-id-${tx.id}`}>#{tx.id}</span>
                                        </td>
                                        <td>
                                            <div className="payments-when">
                                                {created.toLocaleDateString(undefined, {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year:
                                                        created.getFullYear() ===
                                                        new Date().getFullYear()
                                                            ? undefined
                                                            : 'numeric',
                                                })}
                                            </div>
                                            <div className="payments-when__time">
                                                {formatClock(created, timeFmt)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="payments-cust">
                                                {tx.customerName || '—'}
                                            </div>
                                            {tx.description && (
                                                <div className="payments-cust__sub">
                                                    {tx.description}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {/* Appointment column (merged): #id link + booking status +
                                                payment-rollup status + remaining, all stacked together. */}
                                            {tx.appointmentId ? (
                                                <div className="payments-appt-cell">
                                                    <button
                                                        type="button"
                                                        className="payments-appt-link"
                                                        onClick={() => openAppointmentDetail(tx.appointmentId!)}
                                                        title={t('payments.openAppointment')}
                                                    >
                                                        #{tx.appointmentId}
                                                    </button>
                                                    {(() => {
                                                        const appt = appointmentMap[tx.appointmentId!];
                                                        if (!appt) return null;
                                                        const meta = APPOINTMENT_PAYMENT_LABEL[appt.paymentStatus];
                                                        const remaining = remainingByAppt[tx.appointmentId!];
                                                        const showRemaining =
                                                            remaining != null &&
                                                            (appt.paymentStatus === 'PARTIAL_PAID' ||
                                                                appt.paymentStatus === 'PENDING' ||
                                                                appt.paymentStatus === 'NOT_PAID');
                                                        return (
                                                            <>
                                                                <div className="payments-appt-badges">
                                                                    {appt.status && (
                                                                        <span className={`payments-appt-badge payments-appt-badge--booking-${appt.status.toLowerCase()}`}>
                                                                            {t(`appointments.status.${appt.status}`, { defaultValue: appt.status })}
                                                                        </span>
                                                                    )}
                                                                    {meta && (
                                                                        <span className={`payments-appt-badge payments-appt-badge--${meta.tone}`}>
                                                                            {t(`appointments.paymentStatus.${appt.paymentStatus}`, { defaultValue: meta.label })}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {showRemaining && (
                                                                    <div className="payments-appt-remaining">
                                                                        {fmtMoney(remaining, tx.currency)} {t('payments.due')}
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <span className="payments-appt-link payments-appt-link--none">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="payments-method">
                                                {t(`payments.method.${tx.method}`)}
                                            </span>
                                            {tx.giftCardCode && (
                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}
                                                    data-cy="payments-giftcard-cell">
                                                    🎁 {tx.giftCardCode}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="payments-status-cell">
                                                <span
                                                    className={`payments-status payments-status--${tx.status.toLowerCase()}`}
                                                    data-cy={`payments-status-${tx.id}`}
                                                >
                                                    {t(`payments.status.${tx.status}`, { defaultValue: TRANSACTION_STATUS_LABEL[tx.status] })}
                                                </span>
                                                {/* "Deleted" belongs with the payment status, not under the
                                                    client name (where it read as if the client was deleted).
                                                    Wrapped so the inline-block tag stays an atomic inline and
                                                    the deleted-row's line-through doesn't strike it too. */}
                                                {tx.deleted && (
                                                    <div className="payments-deleted-line">
                                                        <span
                                                            className="payments-deleted-tag"
                                                            title={
                                                                tx.deletedOn
                                                                    ? t('payments.deletedTitle', {
                                                                        date: new Date(tx.deletedOn).toLocaleString(),
                                                                        by: tx.deletedBy ? ` by ${tx.deletedBy}` : '',
                                                                      })
                                                                    : t('payments.deleted')
                                                            }
                                                        >
                                                            {t('payments.deleted')}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="payments-table__num">
                                            {fmtMoney(tx.amount, tx.currency)}
                                        </td>
                                        <td className="payments-table__num">
                                            {tx.refundedAmount > 0
                                                ? fmtMoney(tx.refundedAmount, tx.currency)
                                                : '—'}
                                        </td>
                                        <td className="payments-table__num">
                                            {/* Net column subtracts the refunded slice so a
                                                partial refund actually reduces the take-home. */}
                                            {fmtMoney(
                                                Math.max(0, tx.netAmount - (tx.refundedAmount || 0)),
                                                tx.currency
                                            )}
                                        </td>
                                        <td>
                                            {/* Card/Stripe rows can't be edited or
                                                deleted — only refunded. Manual rows
                                                with no refunds support all three. */}
                                            <div className="payments-actions">
                                                {canCheckout &&
                                                    !tx.deleted &&
                                                    tx.method !== 'CARD_STRIPE' &&
                                                    tx.refundedAmount === 0 && (
                                                        <>
                                                            {/* Edit only while PENDING — a received/approved
                                                                (SUCCEEDED) payment is settled and can't be
                                                                silently changed (delete + re-record instead). */}
                                                            {tx.status === 'PENDING' && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-secondary"
                                                                    onClick={() => openEditPayment(tx)}
                                                                    title={t('payments.actions.editTitle')}
                                                                >
                                                                    {t('payments.actions.edit')}
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeletePayment(tx)}
                                                                title={t('payments.actions.deleteTitle')}
                                                            >
                                                                {t('payments.actions.delete')}
                                                            </button>
                                                        </>
                                                    )}
                                                {refundable && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleRefund(tx)}
                                                    >
                                                        {t('payments.actions.refund')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    perPage={perPage}
                    totalItems={total}
                    onPageChange={setPage}
                    onPerPageChange={(n) => {
                        setPerPage(n);
                        setPage(1);
                    }}
                    itemLabel="transaction"
                    perPageOptions={[10, 25, 50, 100]}
                />
            </div>

            {/* ---------- Record manual payment Drawer ---------- */}
            <Drawer
                open={drawerOpen}
                onClose={closeDrawer}
                title={editingId ? t('payments.drawer.editTitle') : t('payments.drawer.newTitle')}
                subtitle={t('payments.drawer.subtitle')}
                size="md"
                footer={
                    <>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSaveManual}
                            disabled={saving}
                        >
                            {saving ? t('common.working') : t('common.save')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={closeDrawer}
                            disabled={saving}
                        >
                            {t('common.cancel')}
                        </button>
                    </>
                }
            >
                {draft && (
                    <>
                        {/* Link this payment to an existing appointment by id.
                            Optional — leave blank for off-calendar payments. */}
                        <div className="form-group">
                            <label>{t('payments.drawer.appointmentId')} <small className="text-muted">({t('common.optional')})</small></label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder={t('payments.drawer.appointmentPlaceholder')}
                                value={draft.appointmentId ?? ''}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        appointmentId: e.target.value || null,
                                    })
                                }
                            />
                        </div>
                        <div className="form-group" style={{ position: 'relative' }}>
                            <label>{t('payments.drawer.customer')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={draft.customerName ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDraft({
                                        ...draft,
                                        customerName: v,
                                        // Typing changes the name → unlink any picked client.
                                        clientId:
                                            draft.clientId &&
                                            (() => {
                                                const linked = clients.find(
                                                    (c) => c.id === draft.clientId
                                                );
                                                const label = linked
                                                    ? `${linked.firstName}${linked.lastName ? ' ' + linked.lastName : ''}`
                                                    : '';
                                                return v === label ? draft.clientId : null;
                                            })(),
                                    });
                                    setShowClientDropdown(true);
                                }}
                                onFocus={() => setShowClientDropdown(true)}
                                onBlur={() =>
                                    setTimeout(() => setShowClientDropdown(false), 150)
                                }
                                placeholder={t('payments.drawer.customerPlaceholder')}
                            />
                            {showClientDropdown && clientMatches.length > 0 && (
                                <div className="client-autocomplete">
                                    {clientMatches.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            className="client-autocomplete-item"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setDraft({
                                                    ...draft,
                                                    customerName: `${c.firstName}${
                                                        c.lastName ? ' ' + c.lastName : ''
                                                    }`,
                                                    clientId: c.id,
                                                });
                                                setShowClientDropdown(false);
                                            }}
                                        >
                                            <div style={{ fontWeight: 600 }}>
                                                {c.firstName}
                                                {c.lastName ? ` ${c.lastName}` : ''}
                                            </div>
                                            <div className="meta" style={{ fontSize: '0.78rem' }}>
                                                {c.email || c.phone || '—'}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {draft.clientId && (
                                <div
                                    style={{
                                        marginTop: '0.35rem',
                                        fontSize: '0.78rem',
                                        color: 'var(--gs-success)',
                                        fontWeight: 600,
                                    }}
                                >
                                    {t('payments.drawer.linkedClient')}
                                </div>
                            )}
                        </div>
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label>
                                    {t('payments.drawer.amount')} <span className="gs-required">*</span>
                                </label>
                                <MoneyInput
                                    currency={draft.currency ?? selectedBusiness?.currency}
                                    value={draft.amount ? String(draft.amount) : ''}
                                    onChange={(next) =>
                                        setDraft({
                                            ...draft,
                                            amount: Number(next) || 0,
                                        })
                                    }
                                />
                            </div>
                            <div className="col-md-6 form-group">
                                <label>{t('payments.drawer.currency')}</label>
                                <select
                                    className="form-select"
                                    value={
                                        draft.currency ??
                                        currencyCode(selectedBusiness?.currency)
                                    }
                                    onChange={(e) =>
                                        setDraft({ ...draft, currency: e.target.value })
                                    }
                                >
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.label} ({c.symbol})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>
                                {t('payments.drawer.method')} <span className="gs-required">*</span>
                            </label>
                            <select
                                className="form-select"
                                value={draft.method}
                                onChange={(e) =>
                                    setDraft({
                                        ...draft,
                                        method: e.target.value as PaymentMethod,
                                    })
                                }
                            >
                                {parseAcceptedMethods(selectedBusiness?.acceptedPaymentMethods, selectedBusiness?.country)
                                    .filter((m) => m !== 'CARD_STRIPE')
                                    .map((m) => (
                                        <option key={m} value={m}>
                                            {t(`payments.method.${m}`)}
                                        </option>
                                    ))}
                            </select>
                            <div className="form-text">
                                {t('payments.drawer.methodHint')}
                            </div>
                            {/* Receiving data for the chosen method — copyable, so the
                                operator can pass it to the client on the spot. */}
                            {draft.method === 'PIX' && (
                                <PayoutInfoHint title={t('settings.payment.pixKeyLabel')}
                                    value={selectedBusiness?.pixKey} dataCy="payments-pix-hint" />
                            )}
                            {draft.method === 'BANK_TRANSFER' && (
                                <PayoutInfoHint title={t('settings.payment.bankInfoLabel')}
                                    value={selectedBusiness?.bankTransferInfo} dataCy="payments-bank-hint" />
                            )}
                            {/* Gift card: the printed code is required at charge time —
                                no redemption engine yet; recorded for reconciliation. */}
                            {draft.method === 'GIFT_CARD' && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <label>{t('payments.giftCard.codeLabel')} <span className="gs-required">*</span></label>
                                    <input type="text" className="form-control" maxLength={80}
                                        value={draft.giftCardCode ?? ''}
                                        onChange={(e) => setDraft({ ...draft, giftCardCode: e.target.value })}
                                        placeholder={t('payments.giftCard.codePlaceholder')}
                                        data-cy="payments-giftcard-code" />
                                    <div className="form-text">{t('payments.giftCard.codeHint')}</div>
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label>{t('payments.drawer.description')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={draft.description ?? ''}
                                onChange={(e) =>
                                    setDraft({ ...draft, description: e.target.value })
                                }
                                placeholder={t('payments.drawer.descriptionPlaceholder')}
                            />
                        </div>
                        <div className="form-group">
                            <label>{t('payments.drawer.notes')}</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={draft.notes ?? ''}
                                onChange={(e) =>
                                    setDraft({ ...draft, notes: e.target.value })
                                }
                            />
                        </div>
                    </>
                )}
            </Drawer>

            {/* Refund modal — replaces the old window.prompt. Empty input ⇒ full refund. */}
            <ConfirmModal
                open={!!refundTarget}
                title={t('payments.refundModal.title')}
                danger
                busy={refundBusy}
                confirmLabel={t('payments.actions.refund')}
                onConfirm={confirmRefund}
                onCancel={() => { if (!refundBusy) setRefundTarget(null); }}
                message={
                    refundTarget && (
                        <>
                            {t('payments.refundModal.body', {
                                customer: refundTarget.customerName || t('payments.refundModal.thisCustomer'),
                                remaining: fmtMoney(refundTarget.amount - refundTarget.refundedAmount, refundTarget.currency),
                            })}
                        </>
                    )
                }
            >
                {refundTarget && (
                    <div className="gs-confirm__field">
                        <label className="form-label">{t('payments.drawer.amount')}</label>
                        <MoneyInput
                            value={refundInput}
                            onChange={setRefundInput}
                            currency={refundTarget.currency}
                            min={0}
                            placeholder={t('payments.refundModal.amountPlaceholder')}
                            autoFocus
                        />
                    </div>
                )}
            </ConfirmModal>

            {/* Delete modal — soft-delete, the row stays in the list struck through. */}
            <ConfirmModal
                open={!!deleteTarget}
                title={t('payments.deleteModal.title')}
                danger
                busy={deleteBusy}
                confirmLabel={t('payments.actions.delete')}
                onConfirm={confirmDelete}
                onCancel={() => { if (!deleteBusy) setDeleteTarget(null); }}
                message={
                    deleteTarget && (
                        <>
                            {t('payments.deleteModal.body', {
                                amount: fmtMoney(deleteTarget.amount, deleteTarget.currency),
                                method: t(`payments.method.${deleteTarget.method}`),
                                customer: deleteTarget.customerName || t('payments.refundModal.thisCustomer'),
                            })}
                            <br />
                            {t('payments.deleteModal.auditNote')}
                        </>
                    )
                }
            />
        </div>
    );
};

export default PaymentsComponent;
